import api from './api';
import { Photo } from './types';
import { compressImage } from '../utils/imageCompression';

/**
 * Reservation Photos Service
 * Handles all reservation photo-related API requests
 */

export interface PhotoUploadData {
    reservation_id: string;
    type: 'check-in' | 'check-out';
    file: File;
}

const photosService = {
    /**
     * Get all photos
     * @returns Promise<Photo[]>
     */
    getAll: async (): Promise<Photo[]> => {
        try {
            const response = await api.get<{ photos: Photo[]; total: number; success: boolean }>('/reservation-photos');
            return response.photos || [];
        } catch (error) {
            console.error('Error fetching all photos:', error);
            throw error;
        }
    },

    /**
     * Get photo by public ID
     * @param publicId - Photo public ID
     * @returns Promise<Photo>
     */
    getById: async (publicId: string): Promise<Photo> => {
        try {
            const response = await api.get<{ photo: Photo; success: boolean }>(`/reservation-photos/${publicId}`);
            return response.photo;
        } catch (error) {
            console.error(`Error fetching photo ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get photos by reservation ID
     * @param reservationId - Reservation public ID
     * @returns Promise<Photo[]>
     */
    getByReservationId: async (reservationId: string): Promise<Photo[]> => {
        try {
            const response = await api.get<{ photos: Photo[]; total: number; success: boolean }>(
                `/reservation-photos/reservation/${reservationId}`
            );
            return response.photos || [];
        } catch (error) {
            console.error(`Error fetching photos for reservation ${reservationId}:`, error);
            throw error;
        }
    },

    /**
     * Get photos by house ID
     * @param houseId - House public ID
     * @returns Promise<Photo[]>
     */
    getByHouseId: async (houseId: string): Promise<Photo[]> => {
        try {
            const response = await api.get<{ photos: Photo[]; total: number; success: boolean }>(
                `/reservation-photos/house/${houseId}`
            );
            return response.photos || [];
        } catch (error) {
            console.error(`Error fetching photos for house ${houseId}:`, error);
            throw error;
        }
    },

    /**
     * Upload a photo with automatic compression to max 2MB
     * @param uploadData - Photo upload data
     * @returns Promise<Photo>
     */
    upload: async (uploadData: PhotoUploadData): Promise<Photo> => {
        try {
            // Compress image to max 2MB
            const compressedFile = await compressImage(uploadData.file, {
                maxSizeMB: 2,
                quality: 0.8,
            });

            // Create FormData
            const formData = new FormData();
            formData.append('photo', compressedFile);
            formData.append('reservation_id', uploadData.reservation_id);
            formData.append('type', uploadData.type);

            // Upload to server
            const response = await api.post<{ photo: Photo; success: boolean; message: string }>(
                '/reservation-photos',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return response.photo;
        } catch (error) {
            console.error('Error uploading photo:', error);
            throw error;
        }
    },

    /**
     * Upload multiple photos
     * @param uploads - Array of photo upload data
     * @returns Promise<Photo[]>
     */
    uploadMultiple: async (uploads: PhotoUploadData[]): Promise<Photo[]> => {
        try {
            const uploadPromises = uploads.map((upload) => photosService.upload(upload));
            const results = await Promise.all(uploadPromises);
            return results;
        } catch (error) {
            console.error('Error uploading multiple photos:', error);
            throw error;
        }
    },

    /**
     * Update a photo
     * @param publicId - Photo public ID
     * @param file - New photo file (optional)
     * @param type - New photo type (optional)
     * @returns Promise<Photo>
     */
    update: async (publicId: string, file?: File, type?: 'check-in' | 'check-out'): Promise<Photo> => {
        try {
            const formData = new FormData();

            if (file) {
                // Compress image to max 2MB
                const compressedFile = await compressImage(file, {
                    maxSizeMB: 2,
                    quality: 0.8,
                });
                formData.append('photo', compressedFile);
            }

            if (type) {
                formData.append('type', type);
            }

            const response = await api.put<{ photo: Photo; success: boolean; message: string }>(
                `/reservation-photos/${publicId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return response.photo;
        } catch (error) {
            console.error(`Error updating photo ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Delete a photo
     * @param publicId - Photo public ID
     * @returns Promise<void>
     */
    delete: async (publicId: string): Promise<void> => {
        try {
            await api.delete(`/reservation-photos/${publicId}`);
        } catch (error) {
            console.error(`Error deleting photo ${publicId}:`, error);
            throw error;
        }
    },
};

export default photosService;